/**
 * Manchester Triage System - Discriminators
 * 
 * This file contains definitions for the standard discriminators used in
 * the Manchester Triage System (MTS) version 3.
 * 
 * Discriminators are the key clinical features that determine the urgency
 * level assigned to a patient within a specific presenting complaint.
 * 
 * Reference: Manchester Triage Group. Emergency Triage: Manchester Triage Group.
 * 3rd ed. Wiley-Blackwell; 2014.
 */

import { MTSDiscriminator, UrgencyLevel } from '@/types/triage';

/**
 * MTS Discriminators - Core discriminators used across presentations
 */
export const MTS_DISCRIMINATORS: MTSDiscriminator[] = [
  // Immediate (Red) discriminators
  {
    id: 'MTS-DISC-001',
    name: 'Airway Compromise',
    description: 'Inability to maintain airway patency requiring immediate intervention',
    urgency: 'immediate',
    rules: []  // Will be populated when rules are defined
  },
  {
    id: 'MTS-DISC-002',
    name: 'Inadequate Breathing',
    description: 'Respiratory effort that fails to maintain adequate oxygenation',
    urgency: 'immediate',
    rules: []
  },
  {
    id: 'MTS-DISC-003',
    name: 'Shock',
    description: 'Clinical syndrome of inadequate tissue perfusion',
    urgency: 'immediate',
    rules: []
  },
  {
    id: 'MTS-DISC-004',
    name: 'Ongoing Seizure',
    description: 'Currently having a generalized seizure',
    urgency: 'immediate',
    rules: []
  },
  {
    id: 'MTS-DISC-005',
    name: 'Unresponsive',
    description: 'No response to voice or pain (AVPU score: U)',
    urgency: 'immediate',
    rules: []
  },
  
  // Very Urgent (Orange) discriminators
  {
    id: 'MTS-DISC-006',
    name: 'Altered Level of Consciousness',
    description: 'GCS less than 13 or only response to voice or pain (AVPU score: P or V)',
    urgency: 'urgent',
    rules: []
  },
  {
    id: 'MTS-DISC-007',
    name: 'Severe Pain',
    description: 'Pain score 8-10/10',
    urgency: 'urgent',
    rules: []
  },
  {
    id: 'MTS-DISC-008',
    name: 'Very Hot',
    description: 'Adult temperature > 38.5°C, Child temperature > 39°C',
    urgency: 'urgent',
    rules: []
  },
  {
    id: 'MTS-DISC-009',
    name: 'Acute Neurological Deficit',
    description: 'New onset of limb weakness, speech disturbance, facial droop',
    urgency: 'urgent',
    rules: []
  },
  {
    id: 'MTS-DISC-010',
    name: 'Chest Pain',
    description: 'Cardiac-sounding chest pain or suspected cardiac origin',
    urgency: 'urgent',
    rules: []
  },
  
  // Urgent (Yellow) discriminators
  {
    id: 'MTS-DISC-011',
    name: 'Moderate Pain',
    description: 'Pain score 5-7/10',
    urgency: 'standard',
    rules: []
  },
  {
    id: 'MTS-DISC-012',
    name: 'Hot',
    description: 'Adult temperature 37.5-38.5°C, Child temperature 38-39°C',
    urgency: 'standard',
    rules: []
  },
  {
    id: 'MTS-DISC-013',
    name: 'Vomiting',
    description: 'Persistent vomiting',
    urgency: 'standard',
    rules: []
  },
  {
    id: 'MTS-DISC-014',
    name: 'Recent Mild Pain',
    description: 'Pain of moderate intensity with onset in last 7 days',
    urgency: 'standard',
    rules: []
  },
  
  // Standard (Green) discriminators
  {
    id: 'MTS-DISC-015',
    name: 'Recent Problem',
    description: 'Symptoms with onset within previous 7 days',
    urgency: 'non-urgent',
    rules: []
  },
  {
    id: 'MTS-DISC-016',
    name: 'Low Pain',
    description: 'Pain score 1-4/10',
    urgency: 'non-urgent',
    rules: []
  },

  // Respiratory specific discriminators
  {
    id: 'MTS-DISC-017',
    name: 'Stridor',
    description: 'Harsh sound on inspiration due to upper airway obstruction',
    urgency: 'immediate',
    rules: []
  },
  {
    id: 'MTS-DISC-018',
    name: 'Audible Wheeze',
    description: 'Expiratory wheeze audible without stethoscope',
    urgency: 'urgent',
    rules: []
  },
  {
    id: 'MTS-DISC-019',
    name: 'Respiratory Exhaustion',
    description: 'Inability to maintain respiratory effort',
    urgency: 'immediate',
    rules: []
  },
  {
    id: 'MTS-DISC-020',
    name: 'Inability to Talk in Sentences',
    description: 'Too breathless to complete sentences in one breath',
    urgency: 'urgent',
    rules: []
  },
  
  // Hemodynamic discriminators
  {
    id: 'MTS-DISC-021',
    name: 'Exsanguinating Hemorrhage',
    description: 'Massive bleeding leading to hemodynamic instability',
    urgency: 'immediate',
    rules: []
  },
  {
    id: 'MTS-DISC-022',
    name: 'Massive Hematemesis',
    description: 'Large volume vomiting of fresh blood',
    urgency: 'immediate',
    rules: []
  },
  {
    id: 'MTS-DISC-023',
    name: 'Fresh Blood Per Rectum',
    description: 'Passing fresh red blood from rectum',
    urgency: 'urgent',
    rules: []
  },
  {
    id: 'MTS-DISC-024',
    name: 'Bradycardia',
    description: 'Adult: HR < 60 bpm, Child: HR below normal for age',
    urgency: 'urgent',
    rules: []
  },
  {
    id: 'MTS-DISC-025',
    name: 'Tachycardia',
    description: 'Adult: HR > 120 bpm, Child: HR above normal for age',
    urgency: 'urgent',
    rules: []
  },
  
  // Neurological discriminators
  {
    id: 'MTS-DISC-026',
    name: 'Pupillary Abnormality',
    description: 'Pupil size, symmetry or reactivity abnormal',
    urgency: 'urgent',
    rules: []
  },
  {
    id: 'MTS-DISC-027',
    name: 'New Onset Confusion',
    description: 'New onset of confusion or disorientation',
    urgency: 'urgent',
    rules: []
  },
  {
    id: 'MTS-DISC-028',
    name: 'History of Unconsciousness',
    description: 'Loss of consciousness at any point during present illness',
    urgency: 'urgent',
    rules: []
  },
  
  // Trauma-specific discriminators
  {
    id: 'MTS-DISC-029',
    name: 'Major Trauma',
    description: 'Severe trauma with potential for multiple serious injuries',
    urgency: 'immediate',
    rules: []
  },
  {
    id: 'MTS-DISC-030',
    name: 'Severe Mechanism of Injury',
    description: 'High-energy impact or substantial force applied to body',
    urgency: 'urgent',
    rules: []
  },
  {
    id: 'MTS-DISC-031',
    name: 'High-Energy Transfer',
    description: 'Mechanism involving significant force or energy',
    urgency: 'urgent',
    rules: []
  },
  {
    id: 'MTS-DISC-032',
    name: 'Critical Skin',
    description: 'Skin injury with neurovascular compromise',
    urgency: 'urgent',
    rules: []
  },
  {
    id: 'MTS-DISC-033',
    name: 'Deformity',
    description: 'Abnormal angulation, rotation or displacement of bone',
    urgency: 'standard',
    rules: []
  },
  
  // Pediatric specific discriminators
  {
    id: 'MTS-DISC-034',
    name: 'Inconsolable by Parents',
    description: 'Child remains distressed despite parental comfort',
    urgency: 'urgent',
    rules: []
  },
  {
    id: 'MTS-DISC-035',
    name: 'Inadequate Feeding in Infant',
    description: 'Reduced intake less than 50% of normal in infant',
    urgency: 'standard',
    rules: []
  },
  {
    id: 'MTS-DISC-036',
    name: 'Floppy Infant',
    description: 'Infant with abnormally low muscle tone',
    urgency: 'urgent',
    rules: []
  },
  
  // Mental health discriminators
  {
    id: 'MTS-DISC-037',
    name: 'Actively Suicidal',
    description: 'Currently attempting or planning imminent self-harm',
    urgency: 'immediate',
    rules: []
  },
  {
    id: 'MTS-DISC-038',
    name: 'Aggressive',
    description: 'Exhibiting threatening or violent behavior',
    urgency: 'urgent',
    rules: []
  },
  {
    id: 'MTS-DISC-039',
    name: 'Acute Psychosis',
    description: 'Delusions, hallucinations, disorganized thinking',
    urgency: 'urgent',
    rules: []
  },
  
  // Exposure/Environmental discriminators
  {
    id: 'MTS-DISC-040',
    name: 'Chemical Exposure',
    description: 'Contact with potentially harmful chemicals',
    urgency: 'urgent',
    rules: []
  },
  {
    id: 'MTS-DISC-041',
    name: 'Hypothermia',
    description: 'Core temperature < 35°C',
    urgency: 'urgent',
    rules: []
  },
  {
    id: 'MTS-DISC-042',
    name: 'Electrical Injury',
    description: 'Injury from electrical current',
    urgency: 'urgent',
    rules: []
  },
  
  // Special cases
  {
    id: 'MTS-DISC-043',
    name: 'Pregnancy > 20 weeks with Abdominal Pain',
    description: 'Abdominal pain in pregnancy beyond 20 weeks',
    urgency: 'urgent',
    rules: []
  },
  {
    id: 'MTS-DISC-044',
    name: 'Acute Onset After Injury',
    description: 'Symptoms beginning immediately after traumatic event',
    urgency: 'standard',
    rules: []
  },
  {
    id: 'MTS-DISC-045',
    name: 'Central Pulse Absent',
    description: 'No palpable pulse at major artery (carotid or femoral)',
    urgency: 'immediate',
    rules: []
  }
];

/**
 * Helper function to find a discriminator by ID
 */
export function findDiscriminatorById(id: string): MTSDiscriminator | undefined {
  return MTS_DISCRIMINATORS.find(discriminator => discriminator.id === id);
}

/**
 * Helper function to find a discriminator by name (case-insensitive)
 */
export function findDiscriminatorByName(name: string): MTSDiscriminator | undefined {
  return MTS_DISCRIMINATORS.find(
    discriminator => discriminator.name.toLowerCase() === name.toLowerCase()
  );
}

/**
 * Group discriminators by urgency level
 */
export function getDiscriminatorsByUrgency(urgency: UrgencyLevel): MTSDiscriminator[] {
  return MTS_DISCRIMINATORS.filter(
    discriminator => discriminator.urgency === urgency
  );
}

/**
 * Get discriminators applicable to a specific presentation
 */
export function getDiscriminatorsForPresentation(presentationId: string): MTSDiscriminator[] {
  // In a full implementation, this would filter based on a mapping between
  // presentations and discriminators. For now, we'll return all discriminators.
  return MTS_DISCRIMINATORS;
}
